import { useEffect, useCallback, useState, useMemo } from "react";
import { trackEvent } from "helpers/analyticsHelpers";
import SpeedTestWorker from "../helpers/speedtest.worker?worker";
import { convertBpsToMbps, createChannelSetting } from "helpers/resolution";
import { type ChannelSetting } from "views/page-content/platforms/platform/platform-helper";
import { QualitySelectionLabel } from "store/platforms/types";
import { Spinner } from "components/spinners/Spinner";
import { Button } from "components/buttons/Button";
import { useTranslation } from "react-i18next";
import styles from "./styles/useSpeedTest.module.scss";

export enum SpeedTestStatus {
    InProgress = "inprogress",
    Completed = "completed",
    Error = "error"
}

export interface SpeedTestResults {
    upload?: number;
    suggestedSetting?: { name: QualitySelectionLabel; setting: ChannelSetting };
    error?: {
        message: string;
    };
}

export interface UseSpeedTestOptions {
    onCompleted?: (results: SpeedTestResults) => void;
}

export function useSpeedTest({ onCompleted }: UseSpeedTestOptions = {}) {
    const { t } = useTranslation();

    const [worker, setWorker] = useState<Worker | null>(null);

    const [speedTestStatus, setSpeedTestStatus] =
        useState<SpeedTestStatus>(undefined);
    const [results, setResults] = useState<SpeedTestResults | undefined>(
        undefined
    );
    const [onCompleteHasRun, setOnCompleteHasRun] = useState(false);

    useEffect(() => {
        const newWorker = new SpeedTestWorker();

        newWorker.onmessage = (event) => {
            const { type, results } = event.data;

            if (type === "finished") {
                const setting = createChannelSetting(results.upload);

                const params = {
                    ...results,
                    suggestedSetting: {
                        name: `${setting["video-frame-height"]}p`,
                        setting
                    }
                };

                setResults(params);
                setSpeedTestStatus(SpeedTestStatus.Completed);

                trackEvent("Speed test completed", {
                    testResult: params,
                    source: "cloudflare"
                });

                setSpeedTestStatus(SpeedTestStatus.Completed);
            }
        };

        setWorker(newWorker);

        // Cleanup on unmount
        return () => {
            newWorker.terminate();
            setWorker(null);
            setSpeedTestStatus(undefined);
            setResults(undefined);
        };
    }, []);

    const runSpeedTest = useCallback(() => {
        if (!worker || speedTestStatus === SpeedTestStatus.InProgress) return;
        setResults(undefined);
        worker.postMessage({ type: "start" });
        setSpeedTestStatus(SpeedTestStatus.InProgress);
        setOnCompleteHasRun(false);
    }, [speedTestStatus, worker]);

    useEffect(() => {
        // Only automatically run speed test once
        if (speedTestStatus === SpeedTestStatus.Completed) return;
        runSpeedTest();
    }, [runSpeedTest, speedTestStatus]);

    useEffect(() => {
        if (
            speedTestStatus === SpeedTestStatus.Completed &&
            results &&
            !onCompleteHasRun
        ) {
            onCompleted?.(results);
            setOnCompleteHasRun(true);
        }
    }, [results, speedTestStatus, onCompleted, onCompleteHasRun]);

    const SpeedTestDisplay = useMemo(() => {
        return (
            <>
                {speedTestStatus === SpeedTestStatus.InProgress && (
                    <span className={`${styles["speed-test-in-progress"]}`}>
                        <Spinner size={24} inheritColor />
                        <p>{t("video-upload:speed-test-in-progress")}</p>
                    </span>
                )}
                {speedTestStatus === SpeedTestStatus.Completed && (
                    <div className={styles["speed-test-results"]}>
                        <p>
                            {t("video-upload:speed-test-results", {
                                upload: `${convertBpsToMbps(
                                    results?.upload
                                )} Mbps`
                            })}
                        </p>
                        <Button type="link" onClick={runSpeedTest}>
                            {t("video-upload:retry-speed-test")}
                        </Button>
                        <small>
                            {t("video-upload:retry-speed-test-description", {
                                quality: results?.suggestedSetting.name
                            })}
                        </small>
                    </div>
                )}
            </>
        );
    }, [speedTestStatus, t, results, runSpeedTest]);

    return {
        speedTestStatus,
        results,
        runSpeedTest,
        SpeedTestDisplay
    };
}
